import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import toast from 'react-hot-toast';
import { api } from '../../shared/service/dataprovider';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import ConfirmDialog from '../../components/ConfirmDialog';
import InlineEditField from '../../components/InlineEditField';
import { IOSSwitch } from '../../components/switch';
import {
  activeOnRequest,
  beginActiveOff,
  confirmActiveOff,
  filterPropertyCatalog,
  formatCityState,
  flagPatch,
  housingTypePatch,
  payeeEditPatch,
  propertyCatalogHome,
  settingGroupsForProperty,
  submitPayeeActivation,
  inactiveSyncClears,
  syncOffPatch,
  syncOnChoices,
  syncSwitchDisabled,
} from '../propertyCatalog';

export default function PropertyCatalog() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeOff, setActiveOff] = useState(null);
  const [payeeOn, setPayeeOn] = useState(null);
  const [payeeDraft, setPayeeDraft] = useState('');
  const [affordableDraft, setAffordableDraft] = useState(false);
  const [syncOn, setSyncOn] = useState(null);
  const [settingsId, setSettingsId] = useState(null);

  const { data: profile, isFetched: profileFetched } = useQuery({
    queryKey: ['user'],
    queryFn: () => api.get('/v1/admin/profile'),
  });
  const canManagePropertyCatalog = profile?.data?.canManagePropertyCatalog;

  useEffect(() => {
    const home = profileFetched ? propertyCatalogHome(canManagePropertyCatalog) : null;
    if (home) navigate(home, { replace: true });
  }, [profileFetched, canManagePropertyCatalog, navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ['property-catalog'],
    queryFn: () => api.get('/v1/admin/property-catalog'),
    enabled: !!canManagePropertyCatalog,
  });

  const save = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/v1/admin/properties/${id}`, body),
  });

  const settingsRow = (data?.data ?? []).find(row => row.id === settingsId) ?? null;
  const audit = useQuery({
    queryKey: ['property-audit', settingsId],
    queryFn: () => api.get(`/v1/admin/properties/${settingsId}/audit`),
    enabled: !!settingsId && !!canManagePropertyCatalog,
  });

  const rows = filterPropertyCatalog(data?.data ?? [], search);
  const clearedInactiveSync = useRef(new Set());

  const persist = async (id, body) => {
    try {
      await save.mutateAsync({ id, body });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not update the property');
      throw err;
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['property-catalog'] });
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
      await queryClient.invalidateQueries({ queryKey: ['property-audit', id] });
    }
  };

  const onActive = (row, next) => {
    if (!next) {
      setActiveOff(beginActiveOff(row));
      return;
    }
    const plan = activeOnRequest(row);
    if (plan.dialog) {
      setPayeeOn(row);
      setPayeeDraft('');
      setAffordableDraft(!!row.affordableHousing);
      return;
    }
    persist(row.id, plan.patch).catch(() => {});
  };

  useEffect(() => {
    inactiveSyncClears(data?.data ?? []).forEach(item => {
      if (clearedInactiveSync.current.has(item.id)) return;
      clearedInactiveSync.current.add(item.id);
      persist(item.id, item.body).catch(() => {
        clearedInactiveSync.current.delete(item.id);
      });
    });
  }, [data]);

  const onSync = (row, next) => {
    if (!row.isActive) return;
    if (!next) {
      persist(row.id, syncOffPatch()).catch(() => {});
      return;
    }
    setSyncOn(row);
  };

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'abbreviation', header: 'Code' },
    { key: 'location', header: 'City and state', render: row => formatCityState(row) || '—' },
    {
      key: 'payeeId',
      header: 'Paylease ID',
      render: row => (
        <InlineEditField
          value={row.payeeId || ''}
          renderValue={value => value || '—'}
          onSave={async draft => {
            const planned = payeeEditPatch(draft);
            if (planned.error) {
              toast.error(planned.error);
              throw new Error(planned.error);
            }
            await persist(row.id, planned.patch);
          }}
        />
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: row => (
        <Select
          size="small"
          value={row.affordableHousing ? 'affordable' : 'conventional'}
          onChange={event => persist(row.id, housingTypePatch(event.target.value === 'affordable'))}
        >
          <MenuItem value="conventional">Conventional</MenuItem>
          <MenuItem value="affordable">Affordable</MenuItem>
        </Select>
      ),
    },
    {
      key: 'active',
      header: 'Active',
      render: row => (
        <IOSSwitch checked={!!row.isActive} onChange={event => onActive(row, event.target.checked)} inputProps={{ 'aria-label': `Active ${row.name}` }} />
      ),
    },
    {
      key: 'sync',
      header: 'Sync users',
      render: row => (
        <IOSSwitch
          checked={!!row.canSync && !!row.isActive}
          disabled={syncSwitchDisabled(row)}
          onChange={event => onSync(row, event.target.checked)}
          inputProps={{ 'aria-label': `Sync users ${row.name}` }}
        />
      ),
    },
    {
      key: 'settings',
      header: '',
      render: row => (
        <Button size="small" onClick={() => setSettingsId(row.id)}>
          Settings
        </Button>
      ),
    },
  ];

  if (!canManagePropertyCatalog) return null;

  return (
    <Box>
      <PageHeader title="Properties" description="Turn a property on for the portal, set application flags, and review who changed them." />
      <TextField
        size="small"
        placeholder="Search name or code"
        value={search}
        onChange={event => setSearch(event.target.value)}
        sx={{ mb: 2, minWidth: 280 }}
      />
      <DataTable columns={columns} rows={rows} rowKey={row => row.id} loading={isLoading} emptyContent="No properties" minWidth={980} />

      <ConfirmDialog
        open={!!activeOff}
        onClose={() => setActiveOff(null)}
        title={activeOff ? `Turn off ${activeOff.propertyName}?` : ''}
        body={activeOff?.body || ''}
        confirmLabel="Turn off"
        cancelLabel="Cancel"
        destructive
        loading={save.isPending}
        onConfirm={async () => {
          if (!activeOff) return;
          try {
            await persist(activeOff.id, confirmActiveOff());
            setActiveOff(null);
          } catch {
            // persist already toasted
          }
        }}
      />

      <Dialog open={!!payeeOn} onClose={() => setPayeeOn(null)} PaperProps={{ sx: { maxWidth: 472, width: '100%', p: 3 } }}>
        <Stack gap={2}>
          <Typography variant="h4">Turn on {payeeOn?.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            A Paylease ID is required before this property can be active.
          </Typography>
          <TextField label="Paylease ID" value={payeeDraft} onChange={event => setPayeeDraft(event.target.value)} required autoFocus />
          <Select
            size="small"
            value={affordableDraft ? 'affordable' : 'conventional'}
            onChange={event => setAffordableDraft(event.target.value === 'affordable')}
          >
            <MenuItem value="conventional">Conventional</MenuItem>
            <MenuItem value="affordable">Affordable</MenuItem>
          </Select>
          <Stack direction="row" gap={1.25}>
            <Button variant="outlined" fullWidth onClick={() => setPayeeOn(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              disabled={!/^\d+$/.test(payeeDraft.trim()) || save.isPending}
              onClick={async () => {
                try {
                  await persist(payeeOn.id, submitPayeeActivation({ payeeId: payeeDraft, affordableHousing: affordableDraft }));
                  setPayeeOn(null);
                } catch {
                  // persist already toasted
                }
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Dialog>

      <Dialog open={!!syncOn} onClose={() => setSyncOn(null)} PaperProps={{ sx: { maxWidth: 472, width: '100%', p: 3 } }}>
        <Stack gap={2}>
          <Typography variant="h4">Sync users for {syncOn?.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Pull residents now, or wait for the next scheduled sync.
          </Typography>
          <Stack direction="row" gap={1.25}>
            {syncOnChoices().map(choice => (
              <Button
                key={choice.id}
                variant={choice.id === 'now' ? 'contained' : 'outlined'}
                fullWidth
                disabled={save.isPending}
                onClick={async () => {
                  try {
                    await persist(syncOn.id, choice.patch);
                    setSyncOn(null);
                  } catch {
                    // persist already toasted
                  }
                }}
              >
                {choice.label}
              </Button>
            ))}
          </Stack>
          <Button variant="text" onClick={() => setSyncOn(null)}>
            Cancel
          </Button>
        </Stack>
      </Dialog>

      <Dialog open={!!settingsRow} onClose={() => setSettingsId(null)} PaperProps={{ sx: { maxWidth: 640, width: '100%', p: 3 } }}>
        <Stack gap={2}>
          <Typography variant="h4">{settingsRow?.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Pay before also turns on transaction reassociation.
          </Typography>
          {settingGroupsForProperty(settingsRow).map(group => (
            <Stack key={group.id} gap={1}>
              {group.label ? <Typography variant="h5">{group.label}</Typography> : null}
              {group.flags.map(flag => (
                <Stack key={flag.field} direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color={flag.enabled ? 'text.primary' : 'text.disabled'}>
                    {flag.label}
                  </Typography>
                  <IOSSwitch
                    checked={!!settingsRow?.[flag.field]}
                    disabled={!flag.enabled || save.isPending}
                    onChange={event => persist(settingsRow.id, flagPatch(flag.field, event.target.checked)).catch(() => {})}
                    inputProps={{ 'aria-label': `${flag.label} ${settingsRow?.name}` }}
                  />
                </Stack>
              ))}
            </Stack>
          ))}
          <Typography variant="h5">History</Typography>
          {audit.isLoading && <CircularProgress size={20} />}
          {audit.isError && (
            <Typography variant="body2" color="error">
              Could not load history.
            </Typography>
          )}
          {!audit.isLoading && !audit.isError && (audit.data?.data?.events ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No recorded changes yet.
            </Typography>
          )}
          <Stack>
            {(audit.data?.data?.events ?? []).map(event => (
              <Stack key={event.id} direction="row" gap={2} sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ minWidth: 150, color: 'text.secondary' }}>
                  {event.at ? moment(event.at).format('MMM D, YYYY h:mm A') : '—'}
                </Typography>
                <Box>
                  <Typography variant="body2">{event.summary}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.actorEmail || '—'}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
          <Button variant="outlined" onClick={() => setSettingsId(null)}>
            Close
          </Button>
        </Stack>
      </Dialog>
    </Box>
  );
}
